import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import {
  contactsModalBodyClass,
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import {
  campaignScheduleUiIssue,
  formatCampaignLocalDateTime,
} from "./campaign-review-presentation.js";
import { CampaignModalContent } from "./CampaignModalContent.js";

const CampaignScheduleFields = lazy(() =>
  import("./CampaignScheduleFields.js").then((module) => ({
    default: module.CampaignScheduleFields,
  })),
);

export function CampaignScheduleDialog({
  open,
  timezone,
  initialDate,
  initialTime,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  timezone: string;
  initialDate: string;
  initialTime: string;
  onOpenChange: (open: boolean) => void;
  onSave: (input: { localDate: string; localTime: string }) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [localDate, setLocalDate] = useState(initialDate);
  const [localTime, setLocalTime] = useState(initialTime);

  useEffect(() => {
    if (!open) return;
    setLocalDate(initialDate);
    setLocalTime(initialTime);
  }, [open, initialDate, initialTime]);

  const issue = campaignScheduleUiIssue({
    localDate,
    localTime,
    timezone,
  });
  const summary = formatCampaignLocalDateTime(localDate, localTime, i18n.language);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <CampaignModalContent size="md" data-testid="campaign-schedule-dialog">
        <div className={contactsModalHeaderClass}>
          <div className="flex items-start gap-2.5">
            <span
              className="inline-grid size-8 shrink-0 place-items-center rounded-md border border-line/70 bg-surface-muted text-muted"
              aria-hidden="true"
            >
              <CalendarClock size={16} />
            </span>
            <div className="grid min-w-0 gap-1">
              <AlertDialogTitle>{t("campaigns.review.launch.scheduleDialogTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("campaigns.review.launch.scheduleDialogHint")}
              </AlertDialogDescription>
            </div>
          </div>
        </div>
        <div className={cn(contactsModalBodyClass, "grid gap-3")}>
          <Suspense
            fallback={<p className="m-0 text-[0.8125rem] text-muted">{t("campaigns.loading")}</p>}
          >
            <CampaignScheduleFields
              localDate={localDate}
              localTime={localTime}
              timezone={timezone}
              onChange={({ localDate: nextDate, localTime: nextTime }) => {
                setLocalDate(nextDate);
                setLocalTime(nextTime);
              }}
            />
          </Suspense>
          <p className="m-0 text-[0.75rem] text-muted">
            {t("campaigns.review.launch.timezoneLabel")}:{" "}
            <LtrValue>{timezone}</LtrValue>
          </p>
          <div className="grid gap-0.5">
            <p className="m-0 flex items-center gap-1.5 text-[0.75rem] font-medium">
              <CalendarClock size={12} aria-hidden="true" />
              {t("campaigns.review.launch.scheduledForLabel")}
            </p>
            <p className="m-0 text-[0.8125rem]">
              {summary ?? t("campaigns.review.launch.chooseDateTime")}
            </p>
          </div>
          {issue === "past" ? (
            <p className="m-0 text-[0.75rem] text-warning" data-testid="campaign-schedule-past">
              {t("campaigns.review.launch.schedulePast")}
            </p>
          ) : null}
          {issue === "invalid" ? (
            <p className="m-0 text-[0.75rem] text-warning" data-testid="campaign-schedule-invalid">
              {t("campaigns.review.launch.scheduleInvalid")}
            </p>
          ) : null}
        </div>
        <AlertDialogActions className={contactsModalFooterClass}>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("campaigns.review.launch.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={issue != null}
            onClick={() => {
              if (issue != null) return;
              onSave({ localDate, localTime });
              onOpenChange(false);
            }}
            data-testid="campaign-schedule-save"
          >
            {t("campaigns.review.launch.saveSchedule")}
          </Button>
        </AlertDialogActions>
      </CampaignModalContent>
    </AlertDialog>
  );
}
